import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs/internal/Observable';
import {environment} from '../../../../../environments/environment';
import {
  BillingInformationDto,
  CompanyRequisitesDto,
  ExpansionPayloadDto,
  SubscriptionStatusDto,
  UpgradePeriodPayloadDto,
} from '@core/models/payment.model';
import {InvoiceDetailsModel, InvoiceModel, NewInvoiceModel, ResponseCreateInvoiceModel} from '@core/models/invoice.model';

/**
 * Фасад над эндпоинтами платёжного модуля — единая точка для paywall-компонентов.
 */
@Injectable({providedIn: 'root'})
export class PaymentFacadeService {
  private readonly base = environment.apiUrl;

  constructor(private http: HttpClient) {
  }

  subscriptionStatus(companyId: string): Observable<SubscriptionStatusDto> {
    return this.http.get<SubscriptionStatusDto>(`${this.base}payment/subscription/status`, {
      params: {companyId},
    });
  }

  invoice(id: string): Observable<InvoiceDetailsModel> {
    return this.http.get<InvoiceDetailsModel>(`${this.base}payment/invoices/${id}`);
  }

  /** Список счетов компании (история платежей). */
  invoices(): Observable<InvoiceModel[]> {
    return this.http.get<InvoiceModel[]>(`${this.base}payment/invoices`);
  }

  createInvoice(payload: NewInvoiceModel): Observable<ResponseCreateInvoiceModel> {
    return this.http.post<ResponseCreateInvoiceModel>(`${this.base}payment/invoices`, payload);
  }

  createExpansion(payload: ExpansionPayloadDto): Observable<ResponseCreateInvoiceModel> {
    return this.http.post<ResponseCreateInvoiceModel>(`${this.base}payment/invoice/expansion`, payload);
  }

  upgradePeriod(payload: UpgradePeriodPayloadDto): Observable<ResponseCreateInvoiceModel> {
    return this.http.post<ResponseCreateInvoiceModel>(`${this.base}payment/invoices`, {
      ...payload,
      kind: 'upgrade_period',
    });
  }

  tinkoffInit(invoiceId: string): Observable<{ paymentUrl: string }> {
    return this.http.post<{ paymentUrl: string }>(`${this.base}payment/tinkoff/init`, {invoiceId});
  }

  testPay(invoiceId: string): Observable<{ invoiceId: string; status: string }> {
    return this.http.post<{ invoiceId: string; status: string }>(`${this.base}payment/test/pay`, {invoiceId});
  }

  /** Привязанные карты текущего пользователя (для автосписания). */
  billingInformations(): Observable<BillingInformationDto[]> {
    return this.http.get<BillingInformationDto[]>(`${this.base}payment/billing-informations`);
  }

  /** Отвязать карту: удаляет привязку у провайдера, отключает автопродление. */
  deleteBillingInformation(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}payment/billing-informations/${id}`);
  }

  requisites(companyId: string): Observable<CompanyRequisitesDto> {
    return this.http.get<CompanyRequisitesDto>(`${this.base}companies/${companyId}/requisites`);
  }

  updateRequisites(companyId: string, dto: Partial<CompanyRequisitesDto>): Observable<CompanyRequisitesDto> {
    return this.http.put<CompanyRequisitesDto>(`${this.base}companies/${companyId}/requisites`, dto);
  }

  pdfUrl(invoiceId: string): string {
    return `${this.base}payment/invoice/${invoiceId}/pdf`;
  }
}
